/** @format */

import React from "react";
import { useSelector } from "react-redux";
import {
  sendChatMessage,
  analyzeDeeper,
  generateSessionId,
  parseChatError,
  submitFeedback,
} from "./propilotApi";
import { decodeAuthToken } from "../../helpers/UtilityHelper";

function useChatBot({ onAuthFailure }) {
  const accessToken = useSelector(
    (state) => state.user?.userInfo?.accessToken
  );
  const merchantId = useSelector(
    (state) => state.user?.userInfo?.merchant?.merchantId ?? null
  );

  const isProshipAdmin = React.useMemo(() => {
    const { roles = [] } = decodeAuthToken(accessToken);
    return roles.includes("ROLE_PROSHIPADMIN");
  }, [accessToken]);

  const adminMerchantId = isProshipAdmin ? merchantId : null;

  const onAuthFailureRef = React.useRef(onAuthFailure);
  React.useEffect(() => {
    onAuthFailureRef.current = onAuthFailure;
  }, [onAuthFailure]);

  const [turns, setTurns] = React.useState([]);
  const [sessionId, setSessionId] = React.useState(() => generateSessionId());
  const [isFetching, setIsFetching] = React.useState(false);
  const [analyzingTurn, setAnalyzingTurn] = React.useState(null);
  // Successfully answered questions this session — combined with the analytics
  // baseline in index.js to detect milestone crossings (10th question, etc.).
  const [successCount, setSuccessCount] = React.useState(0);
  const fetchingRef = React.useRef(false);

  // Admin "Switch Account" must rotate session_id and clear the thread —
  // DuckDB cache is keyed on session_id, not merchant, so otherwise follow-ups
  // would hit the previous merchant's cached data.
  const previousAdminMerchantRef = React.useRef(adminMerchantId);
  React.useEffect(() => {
    if (!isProshipAdmin) return;
    if (previousAdminMerchantRef.current === adminMerchantId) return;
    previousAdminMerchantRef.current = adminMerchantId;
    setSessionId(generateSessionId());
    setTurns([]);
    setAnalyzingTurn(null);
    setSuccessCount(0);
  }, [isProshipAdmin, adminMerchantId]);

  const handleAuthFailure = (errorCode) => {
    if (onAuthFailureRef.current) onAuthFailureRef.current(errorCode);
  };

  const sendMessage = async (rawText, suggestionAction = null) => {
    const text = (rawText || "").trim();
    if (!text || fetchingRef.current) return;

    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setTurns((t) => [
      ...t,
      { id: turnId, user: text, bot: null, analysis: null },
    ]);
    fetchingRef.current = true;
    setIsFetching(true);

    const setBot = (bot) =>
      setTurns((t) =>
        t.map((turn) => (turn.id === turnId ? { ...turn, bot } : turn))
      );

    try {
      const data = await sendChatMessage({
        sessionId,
        message: text,
        suggestionAction,
        token: accessToken,
        adminMerchantId,
      });
      setBot(data);
      if (data && data.ok !== false && data.type !== "error") {
        setSuccessCount((c) => c + 1);
      }
    } catch (e) {
      console.error("ProPilot /api/proship/chat failed:", e);
      const { errorCode, userMessage, isAuthFailure } = parseChatError(e);
      setBot({
        ok: false,
        type: "error",
        user_message: userMessage,
        message: e?.message,
        error_code: errorCode,
      });
      if (isAuthFailure) handleAuthFailure(errorCode);
    } finally {
      fetchingRef.current = false;
      setIsFetching(false);
    }
  };

  const analyzeTurn = async (turnIndex) => {
    if (analyzingTurn !== null) return;
    setAnalyzingTurn(turnIndex);
    try {
      const data = await analyzeDeeper({
        sessionId,
        turnIndex,
        token: accessToken,
      });
      setTurns((t) =>
        t.map((turn, idx) =>
          idx === turnIndex ? { ...turn, analysis: data } : turn
        )
      );
    } catch (e) {
      console.error("ProPilot /api/chat/analyze failed:", e);
      const { errorCode, userMessage, isAuthFailure } = parseChatError(e);
      setTurns((t) =>
        t.map((turn, idx) =>
          idx === turnIndex
            ? {
                ...turn,
                analysis: {
                  ok: false,
                  type: "error",
                  user_message: userMessage,
                  error_code: errorCode,
                },
              }
            : turn
        )
      );
      if (isAuthFailure) handleAuthFailure(errorCode);
    } finally {
      setAnalyzingTurn(null);
    }
  };

  // Thumbs up/down on a specific answer. Optimistically marks the turn so the
  // UI updates instantly, then persists to /api/feedback. On failure the
  // optimistic state is rolled back so the user can retry. A second click on
  // the same rating clears the vote (toggle off). `comment` carries an
  // optional downvote reason ("reason:wrong_numbers" or free text).
  const submitTurnFeedback = async (turnIndex, rating, comment = null) => {
    let previous = null;
    let nextRating = rating;
    setTurns((t) =>
      t.map((turn, idx) => {
        if (idx !== turnIndex) return turn;
        previous = turn.feedback || null;
        // A comment re-affirms the existing vote (reason chip after a
        // downvote) — only a bare same-rating click toggles the vote off.
        nextRating = !comment && turn.feedback === rating ? null : rating;
        return { ...turn, feedback: nextRating, feedbackError: false };
      })
    );

    // Toggling a vote fully off — nothing to persist (we still keep the prior
    // record server-side; clearing is a UI affordance only).
    if (nextRating === null) return;

    const targetTurn = turns[turnIndex];
    try {
      await submitFeedback({
        sessionId,
        turnIndex,
        rating: nextRating,
        question: targetTurn?.user,
        comment: comment || undefined,
        responseType: targetTurn?.bot?.type,
        intent: targetTurn?.bot?.meta?.intent,
        token: accessToken,
      });
    } catch (e) {
      console.error("ProPilot /api/feedback failed:", e);
      setTurns((t) =>
        t.map((turn, idx) =>
          idx === turnIndex
            ? { ...turn, feedback: previous, feedbackError: true }
            : turn
        )
      );
    }
  };

  const resetSession = () => {
    setSessionId(generateSessionId());
    setTurns([]);
    setAnalyzingTurn(null);
    setSuccessCount(0);
  };

  return {
    turns,
    sessionId,
    isFetching,
    analyzingTurn,
    successCount,
    sendMessage,
    analyzeTurn,
    submitTurnFeedback,
    resetSession,
    // Admins query the merchant they've switched to; null for regular users
    // (the backend then uses the token's own merchant). Exposed so the
    // facts fetch in index.js targets the same merchant as chat.
    adminMerchantId,
  };
}

export default useChatBot;
